import { SelectField } from '@app/components/form/FormSelect';
import { leaseTerminationReasonOptions, NSW_STATE, NswTerminationReason } from '@app/config';
import React, { FC } from 'react';
import { Textarea } from '../buildADoc/components/Textarea';
import DatePickerInAgencyTimeZone from '@app/components/form/DatePickerInAgencyTimeZone';
import Note, { NoteType } from '@app/common/components/Note';
import { FormText } from '@app/components/form/FormText';

import styles from './NswNoticeReasons.module.scss';

interface AdditionalReasonFieldsProps {
    reason?: NswTerminationReason;
    disabled?: boolean;
    values: Record<string, unknown>;
}

const AdditionalReasonFields: React.FC<AdditionalReasonFieldsProps> = ({ reason, disabled, values }) => {
    if (!reason) {
        return null;
    }

    switch (reason) {
        case NswTerminationReason.Breach:
            return (
                <>
                    <FormText
                        key="breachedClauses"
                        label="Breached clause/s"
                        name="breachedClauses"
                        disabled={disabled}
                        required
                    />
                    <Textarea
                        key="detailsOfBreach"
                        label="The conduct causing the breach is/was"
                        name="detailsOfBreach"
                        disabled={disabled}
                        required
                    />
                </>
            );

        case NswTerminationReason.HasSold:
            return (
                <DatePickerInAgencyTimeZone
                    key="saleDate"
                    label="Property exchange date"
                    name="saleDate"
                    disabled={disabled}
                    selected={values.saleDate}
                    required
                />
            );

        case NswTerminationReason.LandlordResidence:
            return (
                <DatePickerInAgencyTimeZone
                    key="landlordWillResideFromDate"
                    label="Date of Occupancy by Landlord/Family"
                    name="landlordWillResideFromDate"
                    disabled={disabled}
                    selected={values.landlordWillResideFromDate}
                    required
                />
            );

        case NswTerminationReason.AffordableHousing:
            return (
                <DatePickerInAgencyTimeZone
                    key="tenantNoLongerEligibleAffordableHousingDate"
                    label="Scheme eligibility end date"
                    name="tenantNoLongerEligibleAffordableHousingDate"
                    disabled={disabled}
                    selected={values.tenantNoLongerEligibleAffordableHousingDate}
                    required
                />
            );

        case NswTerminationReason.TransitionalHousing:
            return (
                <DatePickerInAgencyTimeZone
                    key="tenantNoLongerEligibleTransitionalHousingDate"
                    label="Program eligibility end date"
                    name="tenantNoLongerEligibleTransitionalHousingDate"
                    disabled={disabled}
                    selected={values.tenantNoLongerEligibleTransitionalHousingDate}
                    required
                />
            );

        case NswTerminationReason.ChangeOfUse:
            return (
                <DatePickerInAgencyTimeZone
                    key="premisesChangeOfUseDate"
                    label="Rented premises end date"
                    name="premisesChangeOfUseDate"
                    disabled={disabled}
                    selected={values.premisesChangeOfUseDate}
                    required
                />
            );

        case NswTerminationReason.EmployeeCaretaker:
            return (
                <DatePickerInAgencyTimeZone
                    key="employeeCareTakerArrangementEndDate"
                    label="Arrangement end date"
                    name="employeeCareTakerArrangementEndDate"
                    disabled={disabled}
                    selected={values.employeeCareTakerArrangementEndDate}
                    required
                />
            );
        default:
            return null;
    }
};

type NswSupportingDocumentShortRequirementsProps = {
    reason?: NswTerminationReason;
};

const NswSupportingDocumentShortRequirements: React.FC<NswSupportingDocumentShortRequirementsProps> = ({ reason }) => {
    switch (reason) {
        case NswTerminationReason.HasSold:
            return (
                <Note title="Note - Supporting Documents" type={NoteType.WARNING2}>
                    <div className={styles.requirements}>
                        You are required to provide at least one supporting document when serving a Termination Notice.
                        This should include either a:
                        <ul>
                            <li>
                                <strong>Copy of the contract for sale (or part)</strong> - Shows vendor and purchaser
                                details, property address, contract and completion dates, and confirms vacant possession
                            </li>
                            <li>
                                <strong>Written statement from vendor's solicitor or conveyancer</strong> - confirms a
                                sale contract exists, with vendor name, property address, dates, and requirements for
                                vacant possession.
                            </li>
                        </ul>
                    </div>
                </Note>
            );

        case NswTerminationReason.BeingSold:
            return (
                <Note title="Note - Supporting Document" type={NoteType.WARNING2}>
                    <div className={styles.requirements}>
                        You are required to provide at least one supporting document when serving a Termination Notice.
                        This should include either a:
                        <ul>
                            <li>
                                <strong>Proposed contract for sale</strong> – Includes vendor's name, property details,
                                and confirmation of vacant possession.
                            </li>
                            <li>
                                <strong>Sales agency agreement</strong> – Agreement between landlord and agent with
                                property address and agent licence details.
                            </li>
                        </ul>
                    </div>
                </Note>
            );

        case NswTerminationReason.Renovations:
            return (
                <Note title="Note - Supporting Document" type={NoteType.WARNING2}>
                    <div className={styles.requirements}>
                        You are required to provide supporting documents when serving a Termination Notice.
                        <ul>
                            <li>
                                <strong>Mandatory:</strong>
                                <ul>
                                    <li>
                                        <strong>Written statement by the landlord</strong> – Explains why the works are
                                        significant, why vacancy is needed, and when they'll begin.
                                    </li>
                                </ul>
                            </li>
                        </ul>
                    </div>
                </Note>
            );

        case NswTerminationReason.Demolition:
            return (
                <Note title="Note - Supporting Document" type={NoteType.WARNING2}>
                    <div className={styles.requirements}>
                        You are required to provide supporting documents when serving a Termination Notice.
                        <ul>
                            <li>
                                <strong>Mandatory</strong>
                                <ul>
                                    <li>
                                        <strong>Written statement from the landlord</strong> – Signed and dated, stating
                                        the proposed demolition start date.
                                    </li>
                                    <li>
                                        <strong>Contract for demolition</strong> – Includes parties' names, demolition
                                        details, start date, and demolition licence number (if applicable).
                                    </li>
                                </ul>
                            </li>
                            <li>
                                <strong>If applicable</strong>
                                <ul>
                                    <li>
                                        <strong>Approved Development consent</strong>
                                    </li>
                                    <li>
                                        <strong>Development control order</strong>
                                    </li>
                                </ul>
                            </li>
                        </ul>
                    </div>
                </Note>
            );

        case NswTerminationReason.AffordableHousing:
            return (
                <Note title="Note - Supporting Document" type={NoteType.WARNING2}>
                    <div className={styles.requirements}>
                        You are required to provide at least one supporting document when serving a Termination Notice.
                        This should include:
                        <ul>
                            <li>
                                Information about the reason the tenant is no longer eligible to participate in the
                                affordable housing scheme.
                            </li>
                        </ul>
                    </div>
                </Note>
            );

        case NswTerminationReason.TransitionalHousing:
            return (
                <Note title="Note - Supporting Document" type={NoteType.WARNING2}>
                    <div className={styles.requirements}>
                        You are required to provide at least one supporting document when serving a Termination Notice.
                        This should include:
                        <ul>
                            <li>
                                Information about the reason the tenant is no longer eligible to participate in the
                                transitional housing program.
                            </li>
                        </ul>
                    </div>
                </Note>
            );

        case NswTerminationReason.ChangeOfUse:
            return (
                <Note title="Note - Supporting Document" type={NoteType.WARNING2}>
                    <div className={styles.requirements}>
                        You are required to provide supporting documents when serving a Termination Notice.
                        This should include either a:
                        <ul>
                            <li>
                                <strong>Written statement from the landlord</strong> – Signed and dated, stating the new
                                purpose for the premises and confirming it won't be used as rented residential premises
                                for at least 12 months.
                            </li>
                            <li>
                                <strong>If the premises will be used for a business</strong> – Include the business ABN,
                                development consent for the business, and the relevant licence number.
                            </li>
                            <li>
                                <strong>If the premises will be used for short-term rental</strong> – Provide proof of
                                inclusion in the STRA (Short-Term Rental Accommodation) register.
                            </li>
                        </ul>
                    </div>
                </Note>
            );

        case NswTerminationReason.LandlordResidence:
            return (
                <Note title="Note - Supporting Document" type={NoteType.WARNING2}>
                    <div className={styles.requirements}>
                        You are required to provide at least one supporting document when serving a Termination Notice.
                        This should include either a:
                        <ul>
                            <li>
                                <strong>Written statement from the landlord</strong> – Signed and dated, stating the
                                intent to reside for at least 6 months and the relationship with the relevant person.
                            </li>
                            <li>
                                <strong>Written statement from the relevant person</strong> – Signed and dated, stating
                                the intent to reside for at least 6 months and the relationship with the landlord.
                            </li>
                        </ul>
                    </div>
                </Note>
            );

        default:
            return null;
    }
};

interface NswNoticeReasonsProps {
    disabled?: boolean;
    selectedReason?: NswTerminationReason;
    values: Record<string, unknown>;
    isSupportingDocumentRequired?: boolean;
}

const NswNoticeReasons: FC<NswNoticeReasonsProps> = ({
    disabled,
    selectedReason,
    values,
    isSupportingDocumentRequired
}) => {
    return (
        <div className={styles.container}>
            <SelectField
                label="Reason for termination"
                name="reasonForTermination"
                required
                options={leaseTerminationReasonOptions[NSW_STATE]}
                disabled={disabled}
                dataTest="reasonForTermination"
            />
            {isSupportingDocumentRequired && <NswSupportingDocumentShortRequirements reason={selectedReason} />}
            <AdditionalReasonFields reason={selectedReason} disabled={disabled} values={values} />
        </div>
    );
};

export default NswNoticeReasons;
